// ═══════════════════════════════════════════════════════════════════════════
// PAGE ACTIONS — vue « Matrice d'Eisenhower » (mode d'affichage in-page)
// ═══════════════════════════════════════════════════════════════════════════
//
// Rendu de la matrice 2×2 (Urgence × Importance) DANS la page #actions, à la
// place du tableau, sous la barre de filtres. Pilotée par render-page.ts :
//   - les actions reçues sont DÉJÀ filtrées + triées (mêmes filtres que la liste) ;
//   - deux curseurs (importance vertical, urgence horizontal) font varier les
//     seuils → reclassement live des actions (sans recharger).
//
// Bare-refs runtime (ambient globals) : t, esc, fmtDate.

import {
  bucketByQuadrant,
  effectiveDeadline,
  remainingDays,
  type EisenhowerThresholds,
  type EisenhowerQuadrant,
} from '../../domain/eisenhower.js';

interface MatrixViewAction {
  id?: number;
  text?: string;
  priority?: number | null;
  deadline?: string | null;
  deadline_revised?: string | null;
  status_id?: number | null;
  approver_id?: number | null;
  approver_name?: string | null;
}

interface MatrixStatus {
  name?: string;
  color?: string;
  bold?: boolean;
  italic?: boolean;
  strikethrough?: boolean;
}

interface MatrixUser {
  name?: string;
  trigram?: string | null;
}

// Contexte de résolution pour le rendu des cartes (état + approbateur).
interface ChipCtx {
  statusMap: Record<number, MatrixStatus>;
  userMap: Record<number, MatrixUser>;
}

export interface MatrixViewOpts {
  actions: MatrixViewAction[];
  today: string;
  thresholds: EisenhowerThresholds;
  /** Map id→statut (couleur/police), pour afficher l'état comme dans la liste. */
  statusMap: Record<number, MatrixStatus>;
  /** Map id→utilisateur, pour afficher l'approbateur (nom complet). */
  userMap: Record<number, MatrixUser>;
  /** Bornes du curseur urgence (jours). */
  urgencyMaxDays?: number;
  /** Appelé à chaque déplacement de curseur (persistance URL côté render-page). */
  onChange: (t: EisenhowerThresholds) => void;
  /** Clic sur une carte d'action. */
  onOpenAction: (id: number) => void;
}

// Style inline d'un statut — mêmes règles que la liste (component-action-row).
function statusInlineStyle(status: MatrixStatus | undefined | null): string {
  if (!status) return '';
  const parts: string[] = [];
  if (status.color) parts.push('color:' + status.color);
  if (status.bold) parts.push('font-weight:700');
  if (status.italic) parts.push('font-style:italic');
  if (status.strikethrough) parts.push('text-decoration:line-through');
  return parts.join(';');
}

const QUADRANTS: Array<{
  key: EisenhowerQuadrant;
  titleKey: string;
  hintKey: string;
  color: string;
}> = [
  {
    key: 'plan',
    titleKey: 'pageActionsMatrix.q.plan',
    hintKey: 'pageActionsMatrix.q.planHint',
    color: '#2563eb',
  },
  {
    key: 'do',
    titleKey: 'pageActionsMatrix.q.do',
    hintKey: 'pageActionsMatrix.q.doHint',
    color: '#dc2626',
  },
  {
    key: 'eliminate',
    titleKey: 'pageActionsMatrix.q.eliminate',
    hintKey: 'pageActionsMatrix.q.eliminateHint',
    color: '#6b7280',
  },
  {
    key: 'delegate',
    titleKey: 'pageActionsMatrix.q.delegate',
    hintKey: 'pageActionsMatrix.q.delegateHint',
    color: '#d97706',
  },
];

// Hauteur fixe d'une carte (uniforme) pour que la limite « ~3,5 cartes par
// bloc » soit précise quel que soit le contenu. La zone scrollable du bloc est
// plafonnée en conséquence : la demi-carte visible signale qu'il reste des
// actions à faire défiler.
const CHIP_HEIGHT = 64; // px (border-box) : titre 2 lignes + ligne méta
const CHIP_GAP = 6; // px, espace vertical entre cartes
const VISIBLE_ROWS = 3.5;
const BODY_PAD_Y = 8; // px, padding vertical de la zone scrollable
const QUADRANT_BODY_MAX = Math.round(VISIBLE_ROWS * (CHIP_HEIGHT + CHIP_GAP) + BODY_PAD_Y * 2);

function deadlineBadge(a: MatrixViewAction, today: string, urgencyDays: number): string {
  const dl = effectiveDeadline(a);
  if (!dl) {
    return `<span style="color:var(--text3);font-size:11px">${esc(t('pageActionsMatrix.noDeadline'))}</span>`;
  }
  const rd = remainingDays(a, today);
  let label: string;
  let color: string;
  if (rd == null) {
    label = esc(fmtDate(dl));
    color = 'var(--text3)';
  } else if (rd < 0) {
    label = esc(t('pageActionsMatrix.overdue', { date: fmtDate(dl) }));
    color = '#dc2626';
  } else if (rd === 0) {
    label = esc(t('pageActionsMatrix.dueToday'));
    color = '#dc2626';
  } else {
    label = esc(t('pageActionsMatrix.dueIn', { n: rd, date: fmtDate(dl) }));
    color = rd <= urgencyDays ? '#d97706' : 'var(--text3)';
  }
  return `<span style="color:${color};font-size:11px;white-space:nowrap">${label}</span>`;
}

function actionChip(a: MatrixViewAction, today: string, urgencyDays: number, ctx: ChipCtx): string {
  const id = a.id ?? 0;
  const prio = a.priority == null ? 2 : a.priority;
  const prioLabel = t(`actionModal.priority.${prio}`) || String(prio);
  const text = a.text || `#${id}`;
  // État : mêmes règles d'affichage que la liste (couleur/police du statut).
  const status = a.status_id != null ? ctx.statusMap[a.status_id] : undefined;
  const statusHtml = status?.name
    ? `<span style="${statusInlineStyle(status)};font-size:11px;flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${esc(status.name)}</span>`
    : `<span style="font-size:11px;color:var(--text3);flex:1;min-width:0">—</span>`;
  // Approbateur (nom complet) : userMap prioritaire, sinon approver_name.
  const approver =
    (a.approver_id != null ? ctx.userMap[a.approver_id]?.name : undefined) || a.approver_name || '';
  const approverHtml = approver
    ? `<span style="font-size:10px;color:var(--text3);flex-shrink:0;max-width:42%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap" title="${esc(approver)}"><span style="font-weight:600">(A)</span> ${esc(approver)}</span>`
    : '';
  return `<button type="button" class="mtx-chip" data-action-id="${id}"
      style="display:block;box-sizing:border-box;width:100%;height:${CHIP_HEIGHT}px;overflow:hidden;text-align:left;background:var(--bg);border:1px solid var(--border);border-radius:var(--radius);padding:6px 8px;margin-bottom:${CHIP_GAP}px;cursor:pointer;font:inherit;color:var(--text)"
      title="${esc(text)}">
      <div style="display:flex;align-items:flex-start;justify-content:space-between;gap:8px">
        <span style="flex:1;min-width:0;font-size:12px;line-height:1.3;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden"><span style="color:var(--text3);font-weight:600">#${id}</span> ${esc(text)}</span>
        ${approverHtml}
      </div>
      <div style="display:flex;align-items:center;gap:8px;margin-top:4px">
        <span style="font-size:10px;color:var(--text3);flex-shrink:0">${esc(prioLabel)}</span>
        ${statusHtml}
        <span style="flex-shrink:0">${deadlineBadge(a, today, urgencyDays)}</span>
      </div>
    </button>`;
}

function quadrantCell(
  meta: (typeof QUADRANTS)[number],
  actions: MatrixViewAction[],
  today: string,
  urgencyDays: number,
  ctx: ChipCtx,
): string {
  const body = actions.length
    ? actions.map((a) => actionChip(a, today, urgencyDays, ctx)).join('')
    : `<div style="color:var(--text3);font-size:12px;font-style:italic;padding:8px 0">${esc(t('pageActionsMatrix.empty'))}</div>`;
  return `<div style="display:flex;flex-direction:column;border:1px solid var(--border);border-radius:var(--radius);background:var(--bg2);overflow:hidden">
      <div style="padding:8px 10px;border-bottom:1px solid var(--border);border-left:4px solid ${meta.color}">
        <div style="display:flex;align-items:center;justify-content:space-between;gap:8px">
          <strong style="font-size:13px">${esc(t(meta.titleKey))}</strong>
          <span style="font-size:11px;color:var(--text3)">${esc(t('pageActionsMatrix.count', { n: actions.length }))}</span>
        </div>
        <div style="font-size:11px;color:var(--text3);margin-top:2px">${esc(t(meta.hintKey))}</div>
      </div>
      <div style="padding:${BODY_PAD_Y}px 10px;overflow:auto;max-height:${QUADRANT_BODY_MAX}px">${body}</div>
    </div>`;
}

// Construit l'intérieur de la grille 2×2 (recalculé à chaque déplacement de curseur).
function buildGrid(
  actions: MatrixViewAction[],
  today: string,
  thresholds: EisenhowerThresholds,
  ctx: ChipCtx,
): string {
  const buckets = bucketByQuadrant(actions, today, thresholds);
  return QUADRANTS.map((m) =>
    quadrantCell(m, buckets[m.key], today, thresholds.urgencyDays, ctx),
  ).join('');
}

// Libellé repris de la combobox « Priorité » des filtres (pageActions.priority.*)
// pour cohérence de vocabulaire : 0 = « Très haute », 1/2/3 = « … et plus »,
// 4 = « Toutes ».
function priorityFilterLabel(importanceMax: number): string {
  switch (importanceMax) {
    case 0:
      return t('pageActions.priority.0');
    case 1:
      return t('pageActions.priority.1plus');
    case 2:
      return t('pageActions.priority.2plus');
    case 3:
      return t('pageActions.priority.3plus');
    default:
      return t('pageActions.priority.all');
  }
}

function importanceCutoffLabel(importanceMax: number): string {
  return t('pageActionsMatrix.importanceCutoff', { level: priorityFilterLabel(importanceMax) });
}

function urgencyCutoffLabel(urgencyDays: number): string {
  return t('pageActionsMatrix.urgencyCutoff', { n: urgencyDays });
}

// Rend toute la vue matrice dans `el` et câble les curseurs + clics.
export function renderMatrixInto(el: HTMLElement, opts: MatrixViewOpts): void {
  const { actions, today, onChange, onOpenAction } = opts;
  const urgencyMaxDays = opts.urgencyMaxDays ?? 60;
  const ctx: ChipCtx = { statusMap: opts.statusMap, userMap: opts.userMap };
  // Copie locale des seuils (mutée par les curseurs).
  let importanceMax = opts.thresholds.importanceMax;
  let urgencyDays = opts.thresholds.urgencyDays;

  if (!actions.length) {
    el.innerHTML = `<div class="card" style="padding:28px;text-align:center"><p style="margin:0;color:var(--text3)">${esc(t('pageActionsMatrix.emptyAll'))}</p></div>`;
    return;
  }

  // Curseur importance : on raisonne en « niveau d'exigence » croissant vers le
  // haut. impLevel 0 (bas) = tout est important (importanceMax=4) ; impLevel 4
  // (haut) = seule la priorité « Très haute » l'est (importanceMax=0).
  // → importanceMax = 4 - impLevel.
  const impLevel = 4 - importanceMax;

  el.innerHTML = `
    <div>
      <p style="font-size:12px;color:var(--text3);margin:0 0 12px">${esc(t('pageActionsMatrix.legend'))}</p>
      <div style="display:grid;grid-template-columns:auto 1fr;gap:10px;align-items:stretch">
        <!-- Curseur Importance (vertical) + définition juste en dessous -->
        <div style="display:flex;flex-direction:column;align-items:center;justify-content:center;gap:8px">
          <span style="writing-mode:vertical-rl;transform:rotate(180deg);font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.08em;color:var(--text3)">${esc(t('pageActionsMatrix.axisImportance'))} ↑</span>
          <input type="range" id="mtx-imp" min="0" max="4" step="1" value="${impLevel}"
            aria-label="${esc(t('pageActionsMatrix.axisImportance'))}"
            style="writing-mode:vertical-rl;width:26px;height:200px;accent-color:var(--accent);cursor:pointer">
          <span id="mtx-imp-val" style="font-size:11px;color:var(--text2);text-align:center;max-width:110px;line-height:1.25">${esc(importanceCutoffLabel(importanceMax))}</span>
        </div>
        <div>
          <!-- En-têtes de colonnes -->
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:6px">
            <div style="text-align:center;font-size:11px;font-weight:600;color:var(--text3)">${esc(t('pageActionsMatrix.notUrgent'))}</div>
            <div style="text-align:center;font-size:11px;font-weight:600;color:var(--text3)">${esc(t('pageActionsMatrix.urgent'))}</div>
          </div>
          <div id="mtx-grid" style="display:grid;grid-template-columns:1fr 1fr;grid-template-rows:1fr 1fr;gap:10px">${buildGrid(actions, today, { importanceMax, urgencyDays }, ctx)}</div>
          <!-- Curseur Urgence (horizontal) + définition juste en dessous -->
          <div style="margin-top:10px;display:flex;flex-direction:column;align-items:center;gap:4px">
            <input type="range" id="mtx-urg" min="0" max="${urgencyMaxDays}" step="1" value="${urgencyDays}"
              aria-label="${esc(t('pageActionsMatrix.axisUrgency'))}"
              style="width:70%;max-width:520px;accent-color:var(--accent);cursor:pointer">
            <span style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.08em;color:var(--text3)">${esc(t('pageActionsMatrix.axisUrgency'))} →</span>
            <span id="mtx-urg-val" style="font-size:11px;color:var(--text2)">${esc(urgencyCutoffLabel(urgencyDays))}</span>
          </div>
        </div>
      </div>
    </div>`;

  const gridEl = el.querySelector<HTMLElement>('#mtx-grid');
  const impVal = el.querySelector<HTMLElement>('#mtx-imp-val');
  const urgVal = el.querySelector<HTMLElement>('#mtx-urg-val');

  const refresh = (): void => {
    if (gridEl) gridEl.innerHTML = buildGrid(actions, today, { importanceMax, urgencyDays }, ctx);
    if (impVal) impVal.textContent = importanceCutoffLabel(importanceMax);
    if (urgVal) urgVal.textContent = urgencyCutoffLabel(urgencyDays);
    onChange({ importanceMax, urgencyDays });
  };

  el.querySelector<HTMLInputElement>('#mtx-imp')?.addEventListener('input', (e) => {
    const lvl = parseInt((e.target as HTMLInputElement).value);
    importanceMax = 4 - (Number.isFinite(lvl) ? lvl : 4 - importanceMax);
    refresh();
  });
  el.querySelector<HTMLInputElement>('#mtx-urg')?.addEventListener('input', (e) => {
    const v = parseInt((e.target as HTMLInputElement).value);
    urgencyDays = Number.isFinite(v) ? v : urgencyDays;
    refresh();
  });

  gridEl?.addEventListener('click', (e: Event) => {
    const target = e.target as HTMLElement | null;
    const chip = target?.closest<HTMLElement>('[data-action-id]');
    if (!chip) return;
    const id = chip.getAttribute('data-action-id');
    if (id && /^\d+$/.test(id) && id !== '0') onOpenAction(parseInt(id));
  });
}
